import * as PopoverPrimitive from '@radix-ui/react-popover'
import { styled } from '@stitches/react'
import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import useTranslation from 'next-translate/useTranslation'
import { ComponentProps, ReactNode, useCallback, useRef, useState } from 'react'
import { tooltipContentStyle } from '../../constants'

const Content = styled('div', tooltipContentStyle)
const PopoverPrimitiveContent = styled(
  PopoverPrimitive.Content,
  tooltipContentStyle
)

type ContentProps = ComponentProps<typeof PopoverPrimitiveContent>

type Props = {
  className?: string
  children?: React.ReactNode
  content?: ReactNode
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  hideArrow?: boolean
  side?: ContentProps['side']
  align?: ContentProps['align']
  triggerClassName?: string
  portalled?: boolean
}

export const Popover = ({
  className = '',
  children,
  content,
  defaultOpen = false,
  onOpenChange,
  hideArrow,
  side = 'bottom',
  align = 'center',
  triggerClassName = '',
  portalled = true,
}: Props) => {
  const { t } = useTranslation('common')
  const [internalOpen, setInternalOpen] = useState(defaultOpen)

  const clicked = useRef(false)

  const open = useCallback(() => {
    setInternalOpen(true)
  }, [])

  const close = useCallback(() => {
    setInternalOpen(false)
    clicked.current = false
  }, [])

  return (
    <PopoverPrimitive.Root
      open={internalOpen}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
    >
      <PopoverPrimitive.Trigger asChild>
        <div
          className={`outline-none ${triggerClassName}`}
          onMouseEnter={open}
          onMouseLeave={() => {
            if (clicked.current) return
            close()
          }}
          onClick={() => {
            clicked.current = true
            if (!internalOpen) {
              open()
            }
          }}
        >
          {children}

          {!children && (
            <>
              <Icon
                name='tooltip'
                className='cursor-pointer text-cool-500'
                size={16}
              />
              <span className='sr-only'>{t('see_more_details')}</span>
            </>
          )}
        </div>
      </PopoverPrimitive.Trigger>

      {content && portalled ? (
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side={side}
            align={align}
            onMouseEnter={open}
            onMouseLeave={close}
            onInteractOutside={close}
            className={classNames(
              'relative z-[100] cursor-default outline-none',
              {
                'pb-2': side === 'top',
                'pt-2': side === 'bottom',
                '-mr-2 pr-2': side === 'left',
                '-ml-2 pl-2': side === 'right',
              }
            )}
          >
            <Content className={classNames('outline-none', className)}>
              <div className='font-body text-sm outline-none'>{content}</div>

              {!hideArrow && (
                <PopoverPrimitive.Arrow
                  width={16}
                  height={8}
                  className={classNames('fill-cool-50', {
                    '-mt-2': side === 'top' || side === 'bottom',
                  })}
                />
              )}
            </Content>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      ) : (
        <PopoverPrimitive.Content
          side={side}
          align={align}
          onMouseEnter={open}
          onMouseLeave={close}
          onInteractOutside={close}
          className={classNames(
            'relative z-[100] cursor-default outline-none',
            {
              'pb-2': side === 'top',
              'pt-2': side === 'bottom',
              '-mr-2 pr-2': side === 'left',
              '-ml-2 pl-2': side === 'right',
            }
          )}
        >
          <Content className={classNames('outline-none', className)}>
            <div className='font-body text-sm outline-none'>{content}</div>

            {!hideArrow && (
              <PopoverPrimitive.Arrow
                width={16}
                height={8}
                className={classNames('fill-cool-50', {
                  '-mt-2': side === 'top' || side === 'bottom',
                })}
              />
            )}
          </Content>
        </PopoverPrimitive.Content>
      )}
    </PopoverPrimitive.Root>
  )
}
